import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { AlertTriangle } from "lucide-react";
import Button from "../../../../components/Button/Button";
import FormInput from "../../../../components/Form/FormInput";

const CONFIRM_WORD = "DELETE";

// Deleting the account cannot be undone from the portal, so it asks for the
// password and for the word to be typed out - a misclick alone cannot get through.
const DeleteAccountModal = ({ onCancel, onConfirm, submitting }) => {
  const modalRef = useRef(null);
  const [password, setPassword] = useState("");
  const [confirmWord, setConfirmWord] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) onCancel();
    };
    const handleKey = (event) => {
      if (event.key === "Escape") onCancel();
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKey);
    };
  }, [onCancel]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!password) {
      setError("Enter your password to confirm.");
      return;
    }
    if (confirmWord.trim().toUpperCase() !== CONFIRM_WORD) {
      setError(`Type ${CONFIRM_WORD} to confirm.`);
      return;
    }
    const message = await onConfirm(password);
    if (message) setError(message);
  };

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4">
        <motion.div
          ref={modalRef}
          initial={{ opacity: 0, scale: 0.97 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.97 }}
          transition={{ duration: 0.14 }}
          className="w-full max-w-md rounded-2xl bg-white p-5 shadow-pop"
        >
          <div className="mb-4 flex items-start gap-3">
            <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-danger-50 text-danger-600">
              <AlertTriangle size={17} strokeWidth={2} />
            </span>
            <div className="min-w-0 flex-1">
              <h2 className="text-sm font-semibold tracking-tight text-gray-900">
                Delete this account?
              </h2>
              <p className="mt-1 text-[13px] leading-relaxed text-gray-500">
                You and everyone on this account (managers, vendors and clients)
                will be signed out for good and lose access to its data. This
                cannot be undone.
              </p>
            </div>
          </div>

          <form onSubmit={handleSubmit} noValidate className="space-y-4">
            <FormInput
              label="Your password"
              name="password"
              type="password"
              placeholder="Account password"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                setError("");
              }}
              required
            />
            <FormInput
              label={`Type ${CONFIRM_WORD} to confirm`}
              name="confirmWord"
              placeholder={CONFIRM_WORD}
              value={confirmWord}
              onChange={(e) => {
                setConfirmWord(e.target.value);
                setError("");
              }}
              required
            />

            {error && (
              <div className="rounded border border-danger-200 bg-danger-50 px-3 py-2 text-sm text-danger-700">
                {error}
              </div>
            )}

            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={onCancel}
                disabled={submitting}
              >
                Cancel
              </Button>
              <Button type="submit" variant="danger" size="sm" disabled={submitting}>
                {submitting ? "Deleting..." : "Delete account"}
              </Button>
            </div>
          </form>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default DeleteAccountModal;
